import styled, { createGlobalStyle } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html, body {
    height: 100%;
    font-family: ${({ theme }) => theme.typography.fontFamily.base};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: ${({ theme }) => theme.colors.pageBackground};
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.fontSize.md};
    line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  }

  #root {
    height: 100%;
    overflow-x: hidden;
  }

  /* Focus accessibility */
  *:focus {
    outline: ${({ theme }) => theme.accessibility.focusOutline};
    outline-offset: ${({ theme }) => theme.accessibility.focusOffset};
  }

  /* Remove focus outline for mouse users */
  .js-focus-visible *:focus:not(.focus-visible) {
    outline: none;
  }

  /* Touch target minimum size */
  button, a, input, textarea, select {
    min-height: ${({ theme }) => theme.accessibility.minTouchTarget};
  }

  /* Ensure buttons and links have minimum touch target */
  button, a {
    min-width: ${({ theme }) => theme.accessibility.minTouchTarget};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  /* Safe area padding for iOS devices */
  @supports (padding: max(0px)) {
    body {
      padding-left: max(0px, env(safe-area-inset-left));
      padding-right: max(0px, env(safe-area-inset-right));
      padding-bottom: max(0px, env(safe-area-inset-bottom));
    }
  }

  /* Prevent horizontal scroll on mobile */
  html, body {
    overflow-x: hidden;
    width: 100%;
  }

  /* Improve tap highlight on iOS */
  * {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  }

  /* Scrollbar styling for webkit browsers */
  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.medium};
    border-radius: 3px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.border.dark};
  }
`;

// Common styled components
export const Container = styled.div<{ maxWidth?: string; padding?: string }>`
  width: 100%;
  max-width: ${({ maxWidth = '100%' }) => maxWidth};
  margin: 0 auto;
  padding: ${({ padding = '16px' }) => padding};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ padding = '24px' }) => padding};
  }
`;

export const Card = styled.div<{ variant?: 'default' | 'elevated' | 'outlined' }>`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  ${({ variant = 'default', theme }) => {
    switch (variant) {
      case 'elevated':
        return `box-shadow: ${theme.shadows.md};`;
      case 'outlined':
        return `border: 1px solid ${theme.colors.border.light};`;
      default:
        return '';
    }
  }}
`;

export const Button = styled.button<{
  variant?: 'primary' | 'secondary' | 'outline' | 'text';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  /* Size variants */
  ${({ size = 'md', theme }) => {
    switch (size) {
      case 'sm':
        return `
          padding: ${theme.spacing.sm} ${theme.spacing.md};
          font-size: ${theme.typography.fontSize.sm};
          min-height: 36px;
        `;
      case 'lg':
        return `
          padding: ${theme.spacing.lg} ${theme.spacing.xl};
          font-size: ${theme.typography.fontSize.lg};
          min-height: 52px;
        `;
      default:
        return `
          padding: ${theme.spacing.md} ${theme.spacing.lg};
          font-size: ${theme.typography.fontSize.md};
          min-height: 44px;
        `;
    }
  }}
  
  /* Color variants */
  ${({ variant = 'primary', theme }) => {
    switch (variant) {
      case 'secondary':
        return `
          background: ${theme.colors.backgroundSecondary};
          color: ${theme.colors.text.primary};
          
          &:hover:not(:disabled) {
            background: ${theme.colors.border.light};
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${theme.colors.primary};
          border: 1px solid ${theme.colors.primary};
          
          &:hover:not(:disabled) {
            background: ${theme.colors.backgroundAccent};
          }
        `;
      case 'text':
        return `
          background: transparent;
          color: ${theme.colors.primary};
          
          &:hover:not(:disabled) {
            background: ${theme.colors.backgroundAccent};
          }
        `;
      default:
        return `
          background: ${theme.colors.primary};
          color: ${theme.colors.text.inverse};
          
          &:hover:not(:disabled) {
            background: ${theme.colors.primaryDark};
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  
  ${({ fullWidth }) => fullWidth && 'width: 100%;'}
`;

export const Text = styled.p<{
  variant?: 'body' | 'caption' | 'subtitle' | 'title' | 'heading';
  color?: 'primary' | 'secondary' | 'muted' | 'inverse';
  align?: 'left' | 'center' | 'right';
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
}>`
  margin: 0;
  
  ${({ variant = 'body', theme }) => {
    switch (variant) {
      case 'caption':
        return `
          font-size: ${theme.typography.fontSize.xs};
          line-height: ${theme.typography.lineHeight.tight};
        `;
      case 'subtitle':
        return `
          font-size: ${theme.typography.fontSize.lg};
          font-weight: ${theme.typography.fontWeight.medium};
          line-height: ${theme.typography.lineHeight.tight};
        `;
      case 'title':
        return `
          font-size: ${theme.typography.fontSize.xl};
          font-weight: ${theme.typography.fontWeight.semibold};
          line-height: ${theme.typography.lineHeight.tight};
        `;
      case 'heading':
        return `
          font-size: ${theme.typography.fontSize.xxl};
          font-weight: ${theme.typography.fontWeight.bold};
          line-height: ${theme.typography.lineHeight.tight};
        `;
      default:
        return `
          font-size: ${theme.typography.fontSize.md};
          line-height: ${theme.typography.lineHeight.normal};
        `;
    }
  }}
  
  color: ${({ color = 'primary', theme }) => theme.colors.text[color]};
  text-align: ${({ align = 'left' }) => align};
  font-weight: ${({ weight, theme }) => weight ? theme.typography.fontWeight[weight as keyof typeof theme.typography.fontWeight] : undefined};
`;

export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${({ theme }) => theme.colors.border.light};
  border-top: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;